import { atlasBackendApi } from './atlasBackendClient';
import { getAtlasApiBaseUrl } from '../getAtlasApiBaseUrl';

export interface LiftangoInsightsArticle {
  id: string;
  title: string;
  shortDescription: string;
  link: string;
  publishedAt: string;
  imageUrl: string;
}

export interface LiftangoInsightsResponse {
  articles: LiftangoInsightsArticle[];
  source: 'live' | 'fallback';
  fetchedAt: string;
}

const LIFTANGO_RESOURCES_API_BASE = `${getAtlasApiBaseUrl()}/api/liftango-resources`;

const SEEDED_FALLBACK_ARTICLES: LiftangoInsightsArticle[] = [
  {
    id: 'liftango-seed-001',
    title: 'Rising Fuel Prices: How Organisations Can Support Employees and Reduce Commuting Costs',
    shortDescription:
      'With global energy markets under pressure, rising fuel costs are creating challenges for employees and employers. Explore a practical commuting solution.',
    link: 'https://www.liftango.com/resources/rising-fuel-prices-how-organisations-can-support-employees-and-reduce-commuting-costs',
    publishedAt: '2026-03-25T00:00:00+00:00',
    imageUrl: 'https://cdn.prod.website-files.com/5e55bdaa24830b0faa07cb39/69c500141ca8c007ce191d96_engin-akyurt-ATiv-MR0d4U-unsplash.jpg',
  },
  {
    id: 'liftango-seed-002',
    title: 'Global Trends in Ground Access: The Future Of Airport Park & Rides',
    shortDescription:
      'Read how airports are rethinking Park & Ride by moving beyond empty buses and manual operations to smarter demand-led shuttle services.',
    link: 'https://www.liftango.com/resources/global-trends-in-ground-access-the-future-of-airport-park-rides',
    publishedAt: '2026-03-04T00:00:00+00:00',
    imageUrl: 'https://cdn.prod.website-files.com/5e55bdaa24830b0faa07cb39/69a84d6808eaa9521cd93ed9_Airport%20Park%20and%20Ride.jpg',
  },
  {
    id: 'liftango-seed-003',
    title: 'What is Micromobility?',
    shortDescription:
      'Micromobility refers to lightweight transport modes like bikes and scooters. Learn what it is, how it works, and why it matters.',
    link: 'https://www.liftango.com/resources/what-is-micromobility',
    publishedAt: '2026-01-06T00:00:00+00:00',
    imageUrl: 'https://cdn.prod.website-files.com/5e55bdaa24830b0faa07cb39/695cdb3c9f6e5c51dbd33492_marek-rucinski-b0bFZKjWDVY-unsplash.jpg',
  },
  {
    id: 'liftango-seed-004',
    title: 'Is the UK in a Commuter Bus Crisis?',
    shortDescription:
      'As the UK enters 2026, bus commuting faces a critical test. Can smarter, more reliable networks prevent a commuter bus crisis?',
    link: 'https://www.liftango.com/resources/is-the-uk-in-a-commuter-bus-crisis',
    publishedAt: '2026-01-05T00:00:00+00:00',
    imageUrl: 'https://cdn.prod.website-files.com/5e55bdaa24830b0faa07cb39/695be7122f8aa413e7b7b64a_ross-sneddon--gPgeCoA3f4-unsplash.jpg',
  },
  {
    id: 'liftango-seed-005',
    title: 'What is Transit Oriented Development (TOD)?',
    shortDescription:
      'Transit-oriented development explained with key concepts, benefits, and challenges plus shared mobility strategies for sustainable cities.',
    link: 'https://www.liftango.com/resources/what-is-transit-oriented-development-tod',
    publishedAt: '2025-12-10T00:00:00+00:00',
    imageUrl: 'https://cdn.prod.website-files.com/5e55bdaa24830b0faa07cb39/69394fe88f3548c81208a8b8_jocke-wulcan-0Q7yLkGSHpA-unsplash.jpg',
  },
  {
    id: 'liftango-seed-006',
    title: 'How Mobility in Transportation Drives Sustainability',
    shortDescription:
      'Prioritizing mobility in transportation supports long-term sustainability by improving accessibility and giving travelers more options.',
    link: 'https://www.liftango.com/resources/how-mobility-in-transportation-drives-sustainability',
    publishedAt: '2025-11-25T00:00:00+00:00',
    imageUrl: 'https://cdn.prod.website-files.com/5e55bdaa24830b0faa07cb39/69257b2688ab15e2f4dc784e_bernd-dittrich-3EZdYIE2sp0-unsplash.jpg',
  },
];

function isHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function normalizeArticle(raw: unknown): LiftangoInsightsArticle | null {
  const candidate = raw as Partial<LiftangoInsightsArticle> | null;
  if (!candidate) return null;
  if (typeof candidate.id !== 'string' || candidate.id.trim() === '') return null;
  if (typeof candidate.title !== 'string' || candidate.title.trim() === '') return null;
  if (typeof candidate.shortDescription !== 'string' || candidate.shortDescription.trim() === '') return null;
  if (!isHttpUrl(candidate.link)) return null;
  if (!isHttpUrl(candidate.imageUrl)) return null;
  if (typeof candidate.publishedAt !== 'string' || candidate.publishedAt.trim() === '') return null;

  return {
    id: candidate.id,
    title: candidate.title.trim(),
    shortDescription: candidate.shortDescription.trim(),
    link: candidate.link,
    imageUrl: candidate.imageUrl,
    publishedAt: candidate.publishedAt,
  };
}

export async function fetchLiftangoInsights(limit: number = 6): Promise<LiftangoInsightsResponse> {
  const safeLimit = Math.max(1, Math.min(10, Number.isFinite(limit) ? Math.floor(limit) : 6));
  try {
    const response = await atlasBackendApi.get(`${LIFTANGO_RESOURCES_API_BASE}/articles`, {
      params: { limit: safeLimit },
    });

    const normalizedArticles = ((response.data?.articles as unknown[]) ?? [])
      .map(normalizeArticle)
      .filter((item): item is LiftangoInsightsArticle => item !== null);

    if (normalizedArticles.length > 0) {
      return {
        articles: normalizedArticles.slice(0, safeLimit),
        source: response.data?.source === 'live' ? 'live' : 'fallback',
        fetchedAt: typeof response.data?.fetchedAt === 'string'
          ? response.data.fetchedAt
          : new Date().toISOString(),
      };
    }
  } catch {
    // Fallback below
  }

  return {
    articles: SEEDED_FALLBACK_ARTICLES.slice(0, safeLimit),
    source: 'fallback',
    fetchedAt: new Date().toISOString(),
  };
}

